// ════════════════════════════════════════════════════════════════════════
//  Der Zustand der Führung durch die ersten Schritte
//
//  Die Führung läuft über ALLE Seiten mit: sie beginnt auf /classes, geht auf
//  die Codeseite weiter und muss dabei stehen bleiben. Ihr Zustand liegt
//  deshalb in einem Kontext und nicht in einer Seite - dieselbe Überlegung wie
//  bei `DraftContext` in der Admin-App.
//
//  DER PROVIDER SITZT UM DIE ROUTEN, NICHT IN main.tsx
//
//  Ohne Schulprofil gibt es keine Führung: ein Plattformkonto und ein Konto
//  ohne Zuordnung sehen in dieser App eine Erklärung und kein Dashboard. In
//  main.tsx müsste der Provider genau das noch einmal selbst entscheiden.
//
//  DIE FAKTEN KOSTEN KEINE ZUSÄTZLICHE ABFRAGE
//
//  `useClasses` liefert `codeCount` mit und teilt sich den Cache-Schlüssel mit
//  der Klassenliste. Auf /classes ist die Führung damit gratis, auf den anderen
//  Seiten eine Abfrage, die 60 Sekunden frisch bleibt.
//
//  KEIN AUTOSPEICHERN
//
//  Geschrieben wird beim Weiterschalten, beim Weglegen und beim Zurückholen -
//  drei Handgriffe der Lehrkraft, drei Schreibvorgänge. Kein Ereignis, keine
//  Dauer, keine Auswertung.
// ════════════════════════════════════════════════════════════════════════

import { createContext, useContext, useMemo, useState } from "react";
import type { ReactNode } from "react";
import { useSession, type Role } from "@otto/plattform";
import {
  currentStep,
  nextStepKey,
  stepPosition,
  tourChecklist,
  tourRoute,
  tourRunning,
  useSaveTour,
  useTourState,
  type TourCheck as TourCheckBase,
} from "@otto/onboarding";
import { useClasses } from "../../features/classes/lib/data.js";
import { factsFrom } from "../../features/onboarding/lib/facts.js";
import {
  stepsFor,
  tourParams,
  type TourFacts,
  type TourMark,
  type TourStep,
  type TourStepKey,
} from "../../features/onboarding/lib/tour.js";

/** Die Prüfliste dieser App – die Rechnung kommt aus dem Paket. */
export type TourCheck = TourCheckBase<TourStep>;

export type Tour = {
  /** Läuft die Leiste? Falsch heißt: weggelegt oder fertig. */
  readonly running: boolean;
  /** Ist die Führung ganz am Anfang? Dann steht der Willkommenssatz darüber. */
  readonly atStart: boolean;
  /** Die AUFGESCHLAGENE Station: die gewählte, sonst die laufende. */
  readonly step: TourStep;
  /** Alle Stationen mit Haken – die Prüfliste in der Leiste. */
  readonly checklist: readonly TourCheck[];
  /**
   * Steht die aufgeschlagene Station auf dem Lesezeichen?
   *
   * Nur dann gibt es „Angesehen, weiter“: wer eine frühere Station nachliest,
   * darf das Lesezeichen nicht zurückziehen.
   */
  readonly isCurrent: boolean;
  readonly position: { readonly number: number; readonly total: number };
  readonly facts: TourFacts;
  /** Die Zieladresse dieser Station, oder null. */
  readonly route: string | null;
  /** Das hervorzuhebende Bedienelement, oder null. */
  readonly mark: TourMark | null;
  /** Eine Station aufschlagen, um sie noch einmal zu lesen. */
  readonly select: (key: TourStepKey) => void;
  /** Eine angesehene Station abhaken und zur nächsten gehen. */
  readonly advance: () => void;
  /** Die Leiste weglegen – „Später“ und „Fertig“ sind derselbe Vorgang. */
  readonly close: () => void;
  /** Sie zurückholen, vom leisen Verweis im Kopf aus. */
  readonly reopen: () => void;
};

const Context = createContext<Tour | null>(null);

export function TourProvider({
  schoolId,
  userId,
  role,
  children,
}: {
  schoolId: string;
  userId: string;
  role: Role;
  children: ReactNode;
}) {
  const { client } = useSession();
  const classes = useClasses(client, schoolId);
  const stored = useTourState(client, userId, "verwaltung");
  const save = useSaveTour(client, userId, "verwaltung");

  // Welche Station AUFGESCHLAGEN ist, steht nicht in der Datenbank.
  //
  // Nachlesen ist kein Fortschritt: wer Station 2 noch einmal öffnet, hat
  // deswegen nicht weniger geschafft. Das Lesezeichen ist eine Höchstmarke und
  // bleibt es - hier liegt nur, was gerade auf dem Bildschirm offen ist, und
  // das gehört keinem anderen Gerät und keinem anderen Tag.
  //
  // `null` heißt „folge der laufenden Station“: so wandert die Leiste von selbst
  // weiter, sobald die Klasse angelegt ist, ohne dass jemand die Wahl nachziehen
  // muss.
  const [selected, setSelected] = useState<TourStepKey | null>(null);

  const value = useMemo<Tour>(() => {
    const facts = factsFrom(classes.data);
    // `undefined` (lädt noch) und `null` (keine Zeile) laufen bewusst zusammen:
    // in beiden Fällen ist nichts bekannt, und `currentStep` rechnet daraus die
    // erste offene Station. Ein Ladezustand in der Leiste wäre ein Kasten, der
    // für 200 Millisekunden „Einen Moment“ sagt.
    const state = stored.data ?? { step: null, doneAt: null };
    const steps = stepsFor(role);
    const laufend = currentStep(steps, state, facts);
    const checklist = tourChecklist(steps, state, facts);
    // Die aufgeschlagene Station. Eine Wahl, die diese Rolle nicht hat (ein
    // Lesezeichen aus einer anderen Rolle, eine ältere Fassung der App), fällt
    // auf die laufende zurück statt die Leiste leer zu lassen.
    const step = checklist.find((e) => e.step.key === selected)?.step ?? laufend;
    const running = tourRunning(stored.data ?? null);
    return {
      running,
      atStart: state.step === null && !facts.hasClass,
      step,
      checklist,
      isCurrent: step.key === laufend.key,
      // Die Position der LAUFENDEN Station, nicht der aufgeschlagenen: die Zeile
      // sagt, wie weit diese Person ist. Nähme sie die aufgeschlagene, stünde
      // beim Nachlesen von Station 1 „Schritt 1 von 5“ über einer Liste mit zwei
      // Haken - zwei Aussagen über denselben Stand, die sich widersprechen.
      position: stepPosition(steps, laufend.key),
      facts,
      route: tourRoute(step, tourParams(facts)),
      // Nur solange die Leiste läuft. Ein Ring ohne die Erklärung daneben ist
      // eine Markierung, die niemand deuten kann - und genau das stand hier
      // zuerst: „Später“ nahm die Leiste weg und ließ den Ring stehen.
      //
      // Er folgt der AUFGESCHLAGENEN Station, nicht der laufenden: wer Station 1
      // nachliest, will denselben Knopf wiederfinden, um den es damals ging.
      mark: running ? step.mark : null,
      select: (key) => setSelected(key),
      // Weiterschalten heißt auch: die Wahl aufgeben. Sonst bliebe die Leiste
      // auf der Station stehen, die man gerade abgehakt hat.
      advance: () => {
        setSelected(null);
        save.mutate({ step: nextStepKey(steps, laufend.key), doneAt: null });
      },
      // Weggelegt wird an der LAUFENDEN Station, nicht an der aufgeschlagenen:
      // „Später“ beim Nachlesen von Station 1 darf das Lesezeichen nicht
      // dorthin zurückziehen.
      close: () => save.mutate({ step: laufend.key, doneAt: new Date().toISOString() }),
      reopen: () => {
        setSelected(null);
        save.mutate({ step: laufend.key, doneAt: null });
      },
    };
  }, [classes.data, stored.data, role, save, selected]);

  return <TourValueProvider value={value}>{children}</TourValueProvider>;
}

/**
 * Derselbe Kontext mit einem fertigen Wert – für Tests und für alles, was die
 * Leiste ohne Datenbank zeigen soll. Wie `DraftValueProvider` in der Admin-App:
 * eine Komponente wird gegen einen Zustand geprüft, nicht gegen eine Abfrage.
 */
export function TourValueProvider({
  value,
  children,
}: {
  value: Tour;
  children: ReactNode;
}) {
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

/**
 * Der Zustand der Führung, oder `null` außerhalb des Providers.
 *
 * Bewusst `null` statt eines Fehlers: `Shell` wird auch in Tests und
 * Zugangsfällen gerendert, in denen es kein Profil und damit keine Führung
 * gibt. Ein Rahmen, der ohne Führung nicht mehr baut, wäre die falsche
 * Abhängigkeitsrichtung.
 */
export function useTour(): Tour | null {
  return useContext(Context);
}
